import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Users, UserPlus, MessageSquare, Award, ChevronRight,
  MapPin, Briefcase, GraduationCap, Clock, Heart, Share2,
  ArrowLeft, Plus, X, Check, Star, Filter, Building2, Globe,
  Sparkles, Activity, BookOpen, Microscope, Stethoscope, Edit2, Trash2,
  ShieldCheck, Upload, FileText, AlertCircle, CheckCircle2, Loader2
} from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';

import { API } from '../lib/api';

// Subspecialty options
const SUBSPECIALTIES = [
  'Endometriosis',
  'Infertility',
  'Laparoscopy',
  'Cosmetic Gynaecology',
  'High-Risk Pregnancy',
  'Pelvic Floor',
  'Fetal Medicine',
  'Gynec Oncology',
  'Menopause',
  'Adolescent Gynaecology',
  'Robotic Surgery',
  'Reproductive Endocrinology'
];

// Post type options
const POST_TYPES = [
  { id: 'general', label: 'General', icon: MessageSquare },
  { id: 'case_discussion', label: 'Case Discussion', icon: Stethoscope },
  { id: 'conference', label: 'Conference Update', icon: Users },
  { id: 'surgical_insight', label: 'Surgical Insight', icon: Activity },
  { id: 'research', label: 'Research Update', icon: Microscope }
];

// Medical Councils in India
const MEDICAL_COUNCILS = [
  'Medical Council of India (MCI)',
  'National Medical Commission (NMC)',
  'Andhra Pradesh Medical Council',
  'Assam Medical Council',
  'Bihar Medical Council',
  'Chhattisgarh Medical Council',
  'Delhi Medical Council',
  'Goa Medical Council',
  'Gujarat Medical Council',
  'Haryana Medical Council',
  'Himachal Pradesh Medical Council',
  'Jharkhand Medical Council',
  'Karnataka Medical Council',
  'Kerala Medical Council',
  'Madhya Pradesh Medical Council',
  'Maharashtra Medical Council',
  'Manipur Medical Council',
  'Meghalaya Medical Council',
  'Nagaland Medical Council',
  'Odisha Medical Council',
  'Punjab Medical Council',
  'Rajasthan Medical Council',
  'Tamil Nadu Medical Council',
  'Telangana Medical Council',
  'Uttar Pradesh Medical Council',
  'Uttarakhand Medical Council',
  'West Bengal Medical Council'
];

// ====== PROFILE CARD COMPONENT ======
const DoctorCard = ({ doctor, onFollow, onViewProfile, isFollowing }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md hover:border-raspberry-100 transition-all duration-200">
    <div className="flex items-start gap-4">
      {/* Photo */}
      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-raspberry-100 to-pink-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {doctor.photo_url ? (
          <img src={doctor.photo_url} alt={doctor.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-2xl font-bold text-raspberry-800">{doctor.name?.charAt(0)}</span>
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-bold text-gray-900 truncate">{doctor.name}</h3>
          {doctor.is_verified && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">
              <Check className="w-3 h-3" /> Verified
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-2 truncate">{doctor.qualifications}</p>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {doctor.city}, {doctor.country}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> {doctor.years_experience} yrs
          </span>
        </div>
      </div>
    </div>
    
    {/* Subspecialties */}
    {doctor.subspecialties?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mt-4">
        {doctor.subspecialties.slice(0, 3).map((spec, i) => (
          <span key={i} className="text-xs px-2 py-1 bg-sage-50 text-sage-700 rounded-full border border-sage-100">
            {spec}
          </span>
        ))}
        {doctor.subspecialties.length > 3 && (
          <span className="text-xs px-2 py-1 text-gray-400">+{doctor.subspecialties.length - 3}</span>
        )}
      </div>
    )}
    
    {/* Stats & Actions */}
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
      <div className="flex gap-4 text-xs text-gray-400">
        <span>{doctor.followers_count || 0} followers</span>
        <span>{doctor.posts_count || 0} posts</span>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onViewProfile(doctor)}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
        >
          View
        </button>
        <button
          onClick={() => onFollow(doctor.doctor_id)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            isFollowing
              ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              : 'bg-raspberry-800 text-white hover:bg-raspberry-900'
          }`}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </button>
      </div>
    </div>
  </div>
);

// ====== POST CARD COMPONENT ======
const PostCard = ({ post, onLike, onDelete, isOwner }) => {
  const postTypeConfig = POST_TYPES.find(t => t.id === post.post_type) || POST_TYPES[0];
  const PostIcon = postTypeConfig.icon;
  
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-raspberry-100 transition-all duration-200">
      {/* Author Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-raspberry-100 to-pink-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {post.doctor_photo ? (
            <img src={post.doctor_photo} alt={post.doctor_name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-lg font-bold text-raspberry-800">{post.doctor_name?.charAt(0)}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 text-sm">{post.doctor_name}</span>
            {post.is_verified && (
              <span className="flex items-center gap-0.5 px-1 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                <Check className="w-2.5 h-2.5" />
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>{post.doctor_city}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <PostIcon className="w-3 h-3" /> {postTypeConfig.label}
            </span>
          </div>
        </div>
        <span className="text-xs text-gray-400">
          {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </span>
      </div>
      
      {/* Content */}
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{post.content}</p>
      
      {/* Actions */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
        <button
          onClick={() => onLike(post.post_id)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-raspberry-600 transition-colors"
        >
          <Heart className="w-4 h-4" />
          <span>{post.likes_count || 0}</span>
        </button>
        {isOwner && (
          <button
            onClick={() => onDelete(post.post_id)}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

// ====== CREATE PROFILE MODAL ======
const CreateProfileModal = ({ isOpen, onClose, onSave, initialData }) => {
  const [form, setForm] = useState(initialData || {
    name: '',
    email: '',
    city: '',
    country: 'India',
    clinic_hospital: '',
    qualifications: '',
    years_experience: '',
    subspecialties: [],
    bio: ''
  });

  useEffect(() => {
    if (initialData) setForm(initialData);
  }, [initialData]);

  if (!isOpen) return null;

  const toggleSubspecialty = (spec) => {
    setForm(prev => ({
      ...prev,
      subspecialties: prev.subspecialties.includes(spec)
        ? prev.subspecialties.filter(s => s !== spec)
        : [...prev.subspecialties, spec]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.qualifications) {
      toast.error('Please fill in required fields');
      return;
    }
    onSave({
      ...form,
      years_experience: parseInt(form.years_experience) || 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {initialData ? 'Edit Profile' : 'Create Your Profile'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Full Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Dr. Your Name"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300 focus:ring-1 focus:ring-raspberry-100"
              required
            />
          </div>
          
          {/* Email */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email (for notifications)</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="doctor@example.com"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300"
            />
            <p className="text-xs text-gray-400 mt-1">You'll receive verification status updates here</p>
          </div>
          
          {/* Location */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">City *</label>
              <input
                type="text"
                value={form.city}
                onChange={e => setForm(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Mumbai"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Country</label>
              <input
                type="text"
                value={form.country}
                onChange={e => setForm(prev => ({ ...prev, country: e.target.value }))}
                placeholder="India"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300"
              />
            </div>
          </div>
          
          {/* Clinic/Hospital */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Clinic / Hospital</label>
            <input
              type="text"
              value={form.clinic_hospital}
              onChange={e => setForm(prev => ({ ...prev, clinic_hospital: e.target.value }))}
              placeholder="Your Clinic or Hospital Name"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300"
            />
          </div>
          
          {/* Qualifications */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Qualifications *</label>
            <input
              type="text"
              value={form.qualifications}
              onChange={e => setForm(prev => ({ ...prev, qualifications: e.target.value }))}
              placeholder="MD, DGO, FICOG, etc."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300"
              required
            />
          </div>
          
          {/* Years Experience */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Years of Experience</label>
            <input
              type="number"
              value={form.years_experience}
              onChange={e => setForm(prev => ({ ...prev, years_experience: e.target.value }))}
              placeholder="10"
              min="0"
              max="60"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300"
            />
          </div>
          
          {/* Subspecialties */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Subspecialty Interests</label>
            <div className="flex flex-wrap gap-2">
              {SUBSPECIALTIES.map(spec => (
                <button
                  key={spec}
                  type="button"
                  onClick={() => toggleSubspecialty(spec)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    form.subspecialties.includes(spec)
                      ? 'bg-raspberry-800 text-white border-raspberry-800'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-raspberry-200'
                  }`}
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>
          
          {/* Bio */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Short Bio (max 200 words)</label>
            <textarea
              value={form.bio}
              onChange={e => setForm(prev => ({ ...prev, bio: e.target.value }))}
              placeholder="Tell other doctors about yourself..."
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              {form.bio?.split(/\s+/).filter(Boolean).length || 0}/200 words
            </p>
          </div>
          
          {/* Submit */}
          <button
            type="submit"
            className="w-full py-3 bg-raspberry-800 text-white rounded-xl font-semibold hover:bg-raspberry-900 transition-colors"
          >
            {initialData ? 'Save Changes' : 'Create Profile'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ====== CREATE POST MODAL ======
const CreatePostModal = ({ isOpen, onClose, onSave, doctorId }) => {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('general');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error('Please write something');
      return;
    }
    onSave({ content, post_type: postType });
    setContent('');
    setPostType('general');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Create Post</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Post Type */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Post Type</label>
            <div className="flex flex-wrap gap-2">
              {POST_TYPES.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setPostType(type.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    postType === type.id
                      ? 'bg-raspberry-800 text-white border-raspberry-800'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-raspberry-200'
                  }`}
                >
                  <type.icon className="w-3 h-3" />
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Content */}
          <div>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Share a case discussion, conference update, surgical insight, or research finding..."
              rows={5}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300 resize-none"
              autoFocus
            />
          </div>
          
          {/* Submit */}
          <button
            type="submit"
            className="w-full py-3 bg-raspberry-800 text-white rounded-xl font-semibold hover:bg-raspberry-900 transition-colors"
          >
            Post
          </button>
        </form>
      </div>
    </div>
  );
};

// ====== PROFILE DETAIL MODAL ======
const ProfileDetailModal = ({ doctor, isOpen, onClose, onFollow, isFollowing }) => {
  if (!isOpen || !doctor) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Doctor Profile</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-5 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Header */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-raspberry-100 to-pink-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {doctor.photo_url ? (
                <img src={doctor.photo_url} alt={doctor.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold text-raspberry-800">{doctor.name?.charAt(0)}</span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-bold text-gray-900">{doctor.name}</h3>
                {doctor.is_verified && (
                  <span className="flex items-center gap-0.5 px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">
                    <Check className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">{doctor.qualifications}</p>
            </div>
          </div>
          
          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-xs font-medium">Location</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">{doctor.city}, {doctor.country}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs font-medium">Experience</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">{doctor.years_experience} years</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 col-span-2">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Building2 className="w-4 h-4" />
                <span className="text-xs font-medium">Clinic / Hospital</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">{doctor.clinic_hospital || 'Not specified'}</p>
            </div>
          </div>
          
          {/* Subspecialties */}
          {doctor.subspecialties?.length > 0 && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Subspecialties</h4>
              <div className="flex flex-wrap gap-2">
                {doctor.subspecialties.map((spec, i) => (
                  <span key={i} className="px-3 py-1.5 bg-sage-50 text-sage-700 rounded-full text-xs font-medium border border-sage-100">
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Bio */}
          {doctor.bio && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">About</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{doctor.bio}</p>
            </div>
          )}
          
          {/* Stats */}
          <div className="flex gap-6 py-4 border-t border-gray-100 mb-4">
            <div>
              <p className="text-2xl font-bold text-gray-900">{doctor.followers_count || 0}</p>
              <p className="text-xs text-gray-500">Followers</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{doctor.posts_count || 0}</p>
              <p className="text-xs text-gray-500">Posts</p>
            </div>
          </div>
          
          {/* Follow Button */}
          <button
            onClick={() => onFollow(doctor.doctor_id)}
            className={`w-full py-3 rounded-xl font-semibold transition-colors ${
              isFollowing
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-raspberry-800 text-white hover:bg-raspberry-900'
            }`}
          >
            {isFollowing ? 'Following' : 'Follow'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ====== VERIFICATION MODAL ======
const VerificationModal = ({ isOpen, onClose, doctorId, onSuccess }) => {
  const [form, setForm] = useState({
    registration_number: '',
    council_name: '',
    registration_year: new Date().getFullYear()
  });
  const [document, setDocument] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    if (isOpen && doctorId) {
      checkVerificationStatus();
    }
  }, [isOpen, doctorId]);

  const checkVerificationStatus = async () => {
    try {
      const res = await axios.get(`${API}/network/verification/status/${doctorId}`);
      setVerificationStatus(res.data);
    } catch (err) {
      console.error('Failed to check verification status');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or PDF file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setDocument(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setDocumentPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setDocumentPreview(null);
    }
  };

  const uploadDocument = async () => {
    if (!document) return null;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', document);
      formData.append('doctor_id', doctorId);

      const res = await axios.post(`${API}/network/verification/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data.document_url;
    } catch (err) {
      toast.error('Failed to upload document');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.registration_number || !form.council_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload document first if provided
      let documentUrl = null;
      if (document) {
        documentUrl = await uploadDocument();
      }

      // Submit verification request
      const res = await axios.post(`${API}/network/verification/request`, {
        doctor_id: doctorId,
        registration_number: form.registration_number,
        council_name: form.council_name,
        registration_year: parseInt(form.registration_year),
        document_url: documentUrl
      });

      if (res.data.success) {
        toast.success('Verification request submitted successfully!');
        onSuccess?.();
        onClose();
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to submit verification request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Show status if already verified or pending
  if (verificationStatus?.is_verified) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Profile Verified</h2>
            <p className="text-gray-600 mb-4">Your profile has been verified by the Gynovate team.</p>
            {verificationStatus.registration_number && (
              <div className="bg-gray-50 rounded-xl p-4 text-left mb-4">
                <p className="text-xs text-gray-500 mb-1">Registration Number</p>
                <p className="font-semibold text-gray-900">{verificationStatus.registration_number}</p>
                <p className="text-xs text-gray-500 mt-2 mb-1">Medical Council</p>
                <p className="font-semibold text-gray-900">{verificationStatus.council_name}</p>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus?.verification_status === 'pending') {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Pending</h2>
            <p className="text-gray-600 mb-4">Your verification request is being reviewed. We'll update your profile once approved.</p>
            {verificationStatus.registration_number && (
              <div className="bg-gray-50 rounded-xl p-4 text-left mb-4">
                <p className="text-xs text-gray-500 mb-1">Registration Number</p>
                <p className="font-semibold text-gray-900">{verificationStatus.registration_number}</p>
                <p className="text-xs text-gray-500 mt-2 mb-1">Medical Council</p>
                <p className="font-semibold text-gray-900">{verificationStatus.council_name}</p>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Verify Your Profile</h2>
              <p className="text-xs text-gray-500">Get the verified badge</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Why verify?</p>
                <p className="text-xs text-blue-600 mt-1">
                  Verified doctors get a blue badge, appear in Featured Experts, and build more trust with colleagues.
                </p>
              </div>
            </div>
          </div>

          {/* Registration Number */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Medical Registration Number *
            </label>
            <input
              type="text"
              value={form.registration_number}
              onChange={e => setForm(prev => ({ ...prev, registration_number: e.target.value }))}
              placeholder="e.g., MMC/12345/2015"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-300"
              required
            />
          </div>

          {/* Medical Council */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Medical Council *
            </label>
            <select
              value={form.council_name}
              onChange={e => setForm(prev => ({ ...prev, council_name: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-300 bg-white"
              required
            >
              <option value="">Select your medical council</option>
              {MEDICAL_COUNCILS.map(council => (
                <option key={council} value={council}>{council}</option>
              ))}
            </select>
          </div>

          {/* Registration Year */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Year of Registration
            </label>
            <input
              type="number"
              value={form.registration_year}
              onChange={e => setForm(prev => ({ ...prev, registration_year: e.target.value }))}
              min="1960"
              max={new Date().getFullYear()}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-300"
            />
          </div>

          {/* Document Upload */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Upload Certificate (Optional)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Upload your medical council registration certificate for faster verification
            </p>
            
            {document ? (
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  {documentPreview ? (
                    <img src={documentPreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{document.name}</p>
                    <p className="text-xs text-gray-500">{(document.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setDocument(null); setDocumentPreview(null); }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="block border-2 border-dashed border-gray-200 rounded-xl p-6 cursor-pointer hover:border-blue-300 transition-colors">
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Click to upload certificate</p>
                  <p className="text-xs text-gray-400 mt-1">JPEG, PNG, WebP, or PDF (max 5MB)</p>
                </div>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || isUploading}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {(isSubmitting || isUploading) && <Loader2 className="w-4 h-4 animate-spin" />}
            {isUploading ? 'Uploading...' : isSubmitting ? 'Submitting...' : 'Submit for Verification'}
          </button>

          <p className="text-xs text-gray-400 text-center">
            Verification is typically completed within 24-48 hours
          </p>
        </form>
      </div>
    </div>
  );
};

// ====== MAIN NETWORK PAGE ======
export default function NetworkPage() {
  const navigate = useNavigate();
  
  // Current user state (stored in localStorage for open access)
  const [myProfile, setMyProfile] = useState(null);
  const [myDoctorId, setMyDoctorId] = useState(() => localStorage.getItem('gynovate_doctor_id') || null);
  
  // Data states
  const [doctors, setDoctors] = useState([]);
  const [featuredDoctors, setFeaturedDoctors] = useState({});
  const [posts, setPosts] = useState([]);
  const [following, setFollowing] = useState([]);
  const [stats, setStats] = useState({ total_doctors: 0, verified_doctors: 0, total_posts: 0 });
  
  // UI states
  const [activeTab, setActiveTab] = useState('discover'); // discover, feed, featured
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterSubspecialty, setFilterSubspecialty] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal states
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showProfileDetail, setShowProfileDetail] = useState(false);
  const [showVerification, setShowVerification] = useState(false);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Load my profile if I have a doctor ID
  useEffect(() => {
    if (myDoctorId) {
      loadMyProfile();
      loadFollowing();
    }
  }, [myDoctorId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadDoctors(),
        loadFeaturedDoctors(),
        loadPosts(),
        loadStats()
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDoctors = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCity) params.append('city', filterCity);
      if (filterSubspecialty) params.append('subspecialty', filterSubspecialty);
      
      const res = await axios.get(`${API}/network/doctors?${params}`);
      setDoctors(res.data.doctors || []);
    } catch (err) {
      console.error('Failed to load doctors', err);
    }
  };

  const loadFeaturedDoctors = async () => {
    try {
      const res = await axios.get(`${API}/network/doctors/featured`);
      setFeaturedDoctors(res.data || {});
    } catch (err) {
      console.error('Failed to load featured doctors', err);
    }
  };

  const loadPosts = async () => {
    try {
      const res = await axios.get(`${API}/network/posts?limit=30`);
      setPosts(res.data.posts || []);
    } catch (err) {
      console.error('Failed to load posts', err);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/network/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  };

  const loadMyProfile = async () => {
    try {
      const res = await axios.get(`${API}/network/doctors/${myDoctorId}`);
      setMyProfile(res.data);
    } catch (err) {
      // Profile not found, clear stored ID
      localStorage.removeItem('gynovate_doctor_id');
      setMyDoctorId(null);
      setMyProfile(null);
    }
  };

  const loadFollowing = async () => {
    if (!myDoctorId) return;
    try {
      const res = await axios.get(`${API}/network/following/${myDoctorId}`);
      setFollowing(res.data.following_ids || []);
    } catch (err) {
      console.error('Failed to load following', err);
    }
  };

  // Search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      loadDoctors();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, filterCity, filterSubspecialty]);

  // Create profile
  const handleCreateProfile = async (data) => {
    try {
      const res = await axios.post(`${API}/network/doctors`, data);
      const doctorId = res.data.doctor_id;
      localStorage.setItem('gynovate_doctor_id', doctorId);
      setMyDoctorId(doctorId);
      setMyProfile(res.data);
      setShowCreateProfile(false);
      toast.success('Profile created successfully!');
      loadDoctors();
      loadStats();
    } catch (err) {
      toast.error('Failed to create profile');
    }
  };

  // Update profile
  const handleUpdateProfile = async (data) => {
    try {
      const res = await axios.put(`${API}/network/doctors/${myDoctorId}`, data);
      setMyProfile(res.data);
      setShowCreateProfile(false);
      toast.success('Profile updated!');
      loadDoctors();
    } catch (err) {
      toast.error('Failed to update profile');
    }
  };

  // Follow/unfollow
  const handleFollow = async (doctorId) => {
    if (!myDoctorId) {
      toast.info('Create your profile first to follow doctors');
      setShowCreateProfile(true);
      return;
    }
    
    try {
      if (following.includes(doctorId)) {
        await axios.delete(`${API}/network/follow/${doctorId}?follower_id=${myDoctorId}`);
        setFollowing(prev => prev.filter(id => id !== doctorId));
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API}/network/follow?follower_id=${myDoctorId}`, { doctor_id: doctorId });
        setFollowing(prev => [...prev, doctorId]);
        toast.success('Now following!');
      }
      loadDoctors();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  // Create post
  const handleCreatePost = async (data) => {
    if (!myDoctorId) {
      toast.info('Create your profile first to post');
      setShowCreateProfile(true);
      return;
    }
    
    try {
      await axios.post(`${API}/network/posts?doctor_id=${myDoctorId}`, data);
      setShowCreatePost(false);
      toast.success('Post created!');
      loadPosts();
    } catch (err) {
      toast.error('Failed to create post');
    }
  };

  // Like post
  const handleLikePost = async (postId) => {
    try {
      await axios.post(`${API}/network/posts/${postId}/like`);
      setPosts(prev => prev.map(p => 
        p.post_id === postId ? { ...p, likes_count: (p.likes_count || 0) + 1 } : p
      ));
    } catch (err) {
      console.error('Failed to like post');
    }
  };

  // Delete post
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/network/posts/${postId}?doctor_id=${myDoctorId}`);
      toast.success('Post deleted');
      loadPosts();
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  // View profile detail
  const handleViewProfile = (doctor) => {
    setSelectedDoctor(doctor);
    setShowProfileDetail(true);
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="network-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900 font-heading">Gynovate Network</h1>
                <p className="text-xs text-gray-500">Professional Network for Gynaecologists</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {myProfile ? (
                <>
                  {!myProfile.is_verified && (
                    <button
                      onClick={() => setShowVerification(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-xl text-sm font-semibold text-white transition-colors"
                      data-testid="verify-profile-btn"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      <span className="hidden sm:inline">Get Verified</span>
                    </button>
                  )}
                  <button
                    onClick={() => setShowCreateProfile(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Edit Profile</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowCreateProfile(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-raspberry-800 hover:bg-raspberry-900 rounded-xl text-sm font-semibold text-white transition-colors"
                  data-testid="create-profile-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Create Profile</span>
                </button>
              )}
              <button
                onClick={() => navigate('/chat')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 hover:border-raspberry-200 rounded-xl text-sm font-medium text-gray-700 transition-colors"
              >
                <Sparkles className="w-4 h-4 text-raspberry-600" />
                <span className="hidden sm:inline">AI Assistant</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-raspberry-50 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-raspberry-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stats.total_doctors}</p>
                <p className="text-xs text-gray-500">Doctors</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stats.verified_doctors}</p>
                <p className="text-xs text-gray-500">Verified</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sage-50 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-sage-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stats.total_posts}</p>
                <p className="text-xs text-gray-500">Posts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'discover', label: 'Discover Doctors', icon: Search },
            { id: 'feed', label: 'Activity Feed', icon: Activity },
            { id: 'featured', label: 'Featured Experts', icon: Star }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-raspberry-800 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-raspberry-200'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'discover' && (
          <div className="space-y-6">
            {/* Search & Filters */}
            <div className="bg-white rounded-2xl p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search doctors by name..."
                    className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300 focus:bg-white"
                    data-testid="search-input"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-3 rounded-xl border transition-colors ${
                    showFilters || filterCity || filterSubspecialty
                      ? 'bg-raspberry-50 border-raspberry-200 text-raspberry-700'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                </button>
              </div>
              
              {/* Filters */}
              {showFilters && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">City</label>
                    <input
                      type="text"
                      value={filterCity}
                      onChange={e => setFilterCity(e.target.value)}
                      placeholder="Filter by city..."
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">Subspecialty</label>
                    <select
                      value={filterSubspecialty}
                      onChange={e => setFilterSubspecialty(e.target.value)}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-raspberry-300"
                    >
                      <option value="">All subspecialties</option>
                      {SUBSPECIALTIES.map(spec => (
                        <option key={spec} value={spec}>{spec}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Doctors Grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex gap-1.5">
                  {[0, 1, 2].map(i => (
                    <span key={i} className="w-2 h-2 rounded-full bg-raspberry-300 animate-pulse" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            ) : doctors.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {doctors.map(doctor => (
                  <DoctorCard
                    key={doctor.doctor_id}
                    doctor={doctor}
                    onFollow={handleFollow}
                    onViewProfile={handleViewProfile}
                    isFollowing={following.includes(doctor.doctor_id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No doctors found</p>
                <button
                  onClick={() => setShowCreateProfile(true)}
                  className="px-6 py-3 bg-raspberry-800 text-white rounded-xl font-semibold hover:bg-raspberry-900 transition-colors"
                >
                  Be the first to join!
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'feed' && (
          <div className="space-y-6">
            {/* Create Post Button */}
            <button
              onClick={() => myDoctorId ? setShowCreatePost(true) : setShowCreateProfile(true)}
              className="w-full bg-white rounded-2xl p-4 border border-gray-100 hover:border-raspberry-200 transition-colors text-left"
              data-testid="create-post-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-raspberry-50 rounded-xl flex items-center justify-center">
                  <Plus className="w-5 h-5 text-raspberry-600" />
                </div>
                <span className="text-sm text-gray-500">Share a case discussion, conference update, or surgical insight...</span>
              </div>
            </button>

            {/* Posts */}
            {posts.length > 0 ? (
              <div className="space-y-4">
                {posts.map(post => (
                  <PostCard
                    key={post.post_id}
                    post={post}
                    onLike={handleLikePost}
                    onDelete={handleDeletePost}
                    isOwner={post.doctor_id === myDoctorId}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No posts yet</p>
                <button
                  onClick={() => myDoctorId ? setShowCreatePost(true) : setShowCreateProfile(true)}
                  className="px-6 py-3 bg-raspberry-800 text-white rounded-xl font-semibold hover:bg-raspberry-900 transition-colors"
                >
                  Create the first post!
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'featured' && (
          <div className="space-y-8">
            {Object.keys(featuredDoctors).length > 0 ? (
              Object.entries(featuredDoctors).map(([specialty, docs]) => (
                <div key={specialty}>
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-amber-500" />
                    <h2 className="text-lg font-bold text-gray-900">{specialty}</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {docs.map(doctor => (
                      <DoctorCard
                        key={doctor.doctor_id}
                        doctor={doctor}
                        onFollow={handleFollow}
                        onViewProfile={handleViewProfile}
                        isFollowing={following.includes(doctor.doctor_id)}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No featured experts yet</p>
                <p className="text-sm text-gray-400">Create your profile and get verified to be featured!</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <CreateProfileModal
        isOpen={showCreateProfile}
        onClose={() => setShowCreateProfile(false)}
        onSave={myProfile ? handleUpdateProfile : handleCreateProfile}
        initialData={myProfile}
      />
      
      <CreatePostModal
        isOpen={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        onSave={handleCreatePost}
        doctorId={myDoctorId}
      />
      
      <ProfileDetailModal
        doctor={selectedDoctor}
        isOpen={showProfileDetail}
        onClose={() => setShowProfileDetail(false)}
        onFollow={handleFollow}
        isFollowing={selectedDoctor ? following.includes(selectedDoctor.doctor_id) : false}
      />
      
      <VerificationModal
        isOpen={showVerification}
        onClose={() => setShowVerification(false)}
        doctorId={myDoctorId}
        onSuccess={() => {
          loadMyProfile();
          loadStats();
        }}
      />
    </div>
  );
}
